import { FC, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { HttpError } from '@frontend/fetch';
import { sentry } from '@frontend/tracking';
import { useFormsFreemiumFeatureFlag } from '../../providers';
import { DigitalFormsTrialWidget as TrialEligibleWidget } from './trial-eligible';

interface DigitalFormsTrialWidgetProps {
  containerStyles?: Interpolation<Theme>;
  onDismiss?: () => void;
  innerRef?: React.Ref<HTMLDivElement>;
}

const NOTIFICATION_FEATURE = PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_FORMS;
const NOTIFICATION_SOURCE = [PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER];

export const DigitalFormsTrialWidget: FC<DigitalFormsTrialWidgetProps> = ({ containerStyles, onDismiss, innerRef }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { isFreemium } = useFormsFreemiumFeatureFlag();
  const { data: widgetState, isLoading: isLoadingWidgetState } =
    PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
      request: {
        notificationFeature: NOTIFICATION_FEATURE,
        sourceEnum: PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER,
      },
      options: {
        retry: false,
        select: (response) => response.currentStatus,
      },
    });

  const { mutate: toggleWidgetState, isLoading: isWidgetActionInProgress } =
    PlatformLocationFeatureV1.Mutations.useCreateOrUpdateFeatureNotificationUserActionMutation({
      options: {
        onSuccess: onDismiss,
        onError: (error: HttpError) => {
          sentry.warn({
            error: `Error dismissing Forms trial widget from dashboard: ${error?.message}`,
            topic: 'customer-lifecycle-management',
            team: 'forms',
            addContext: {
              name: 'error',
              context: { error },
            },
          });
        },
      },
    });

  const handleDismiss = (action: PlatformLocationFeatureV1.Types.Action) => {
    setIsDismissed(true);

    toggleWidgetState({
      notificationFeature: NOTIFICATION_FEATURE,
      actionEnum: action,
      sourceEnum: NOTIFICATION_SOURCE,
    });
  };

  const handleStartSending = () => {
    setIsDismissed(true);

    toggleWidgetState({
      notificationFeature: NOTIFICATION_FEATURE,
      actionEnum: PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN,
      sourceEnum: NOTIFICATION_SOURCE,
    });
  };

  // Hide widget if user dismissed it, still loading, or has an action recorded (except ACTION_UNKNOWN) and location is not freemium
  const shouldHideWidget =
    !isFreemium ||
    isDismissed ||
    isLoadingWidgetState ||
    widgetState === PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS ||
    widgetState === PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN;

  if (shouldHideWidget) {
    return null;
  }

  return (
    <TrialEligibleWidget
      containerStyles={containerStyles}
      onDismiss={handleDismiss}
      onStartSending={handleStartSending}
      disableDismiss={isWidgetActionInProgress}
      disableStartSending={isWidgetActionInProgress}
      innerRef={innerRef}
    />
  );
};
